import { pick, type KitchenCatalog, type KitchenMaterial, type ZoneKey } from '@cfg/shared';
import { ChevronIcon } from '@cfg/ui';
import { useI18n } from '../i18n';
import { rovingKeyDown } from '../roving';

interface ZoneListProps {
  catalog: KitchenCatalog;
  zones: ZoneKey[];
  active: ZoneKey;
  selections: Partial<Record<ZoneKey, KitchenMaterial>>;
  onSelect: (zone: ZoneKey) => void;
  onHover: (zone: ZoneKey | null) => void;
}

export function ZoneList({ catalog, zones, active, selections, onSelect, onHover }: ZoneListProps) {
  const { t, locale } = useI18n();
  return (
    <nav className="zones" aria-label={t('zones')}>
      <ul
        role="tablist"
        aria-orientation="vertical"
        className="zones__list"
        // arrow keys / Home / End move between the tabs and select them (automatic activation)
        onKeyDown={(e) => rovingKeyDown(e, { selector: '[role="tab"]', activate: true })}
      >
        {catalog.zones
          .filter((z) => zones.includes(z.key))
          .map((zone) => {
            const material = selections[zone.key];
            const selected = zone.key === active;
            return (
              <li key={zone.key} role="presentation">
                <button
                  type="button"
                  role="tab"
                  id={`zone-tab-${zone.key}`}
                  aria-selected={selected}
                  tabIndex={selected ? 0 : -1}
                  aria-controls="material-panel"
                  className="zones__item"
                  onClick={() => onSelect(zone.key)}
                  onMouseEnter={() => onHover(zone.key)}
                  onMouseLeave={() => onHover(null)}
                  onFocus={() => onHover(zone.key)}
                  onBlur={() => onHover(null)}
                >
                  <span
                    className="zones__thumb"
                    style={
                      material
                        ? { backgroundImage: `url("${material.textures.thumbnail}")` }
                        : undefined
                    }
                    aria-hidden
                  />
                  <span className="zones__text">
                    <span className="zones__name">{pick(zone.name, locale)}</span>
                    <span className="zones__material">
                      {material ? pick(material.name, locale) : '—'}
                    </span>
                  </span>
                  <ChevronIcon size={16} className="zones__chevron" />
                </button>
              </li>
            );
          })}
      </ul>
    </nav>
  );
}
